import { isIos } from "./push.ts";

const EDITABLE = "input, textarea, select, [contenteditable=''], [contenteditable='true']";

/**
 * How far the page must be pushed up for the focused field to clear the keyboard: 0 when it is already visible
 * with the page in place. Pure, so it can be tested: `top`/`bottom` are the field's edges with the page unscrolled.
 */
export function neededPan(field: { top: number; bottom: number }, visibleHeight: number, margin = 12): number {
  return Math.max(0, field.bottom + margin - visibleHeight);
}

/**
 * iPad/iPhone: tapping a text box makes Safari push the whole page up so the box clears the keyboard. With a
 * hardware keyboard iOS reports a keyboard area far taller than the small shortcut bar it shows, so a box in the
 * middle of the screen (the new-chat box) sent the page up and left a large empty band at the bottom, with the top of
 * the app off screen. The app never scrolls as a document, so any push is Safari's: it is undone whenever the
 * focused field is visible without it. A field the keyboard really covers (an on-screen keyboard over a box at the
 * bottom) keeps Safari's push, so it stays in view.
 */
export function installIosFocusPan(): void {
  if (!isIos() || !window.visualViewport) return;
  const vv = window.visualViewport;
  let frame = 0;
  const check = () => {
    frame = 0;
    const pushed = window.scrollY || vv.offsetTop;
    if (pushed <= 0) return;
    const el = document.activeElement;
    if (!(el instanceof HTMLElement) || !el.matches(EDITABLE)) {
      window.scrollTo(0, 0); // nothing being typed into: the page has no reason to be pushed
      return;
    }
    const r = el.getBoundingClientRect();
    // the field's place with the page unpushed: rects follow a document scroll, but not a pan of the visual viewport
    const shift = window.scrollY;
    if (neededPan({ top: r.top + shift, bottom: r.bottom + shift }, vv.height) === 0) window.scrollTo(0, 0);
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(check); };
  vv.addEventListener("resize", schedule);
  vv.addEventListener("scroll", schedule);
  window.addEventListener("scroll", schedule, { passive: true });
  // Safari pushes after the focus settles; check again once its scroll animation has run
  document.addEventListener("focusin", () => { schedule(); setTimeout(schedule, 350); });
  document.addEventListener("focusout", () => setTimeout(schedule, 100));
}
